import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Minus, Plus, Bus, Ship, Sparkles, ArrowLeft, Loader2, ExternalLink, CheckCircle2, BadgeCheck, Check, CalendarCheck, ArrowUpRight, PenLine } from "lucide-react";
import { toast } from "sonner";

import { getTicketProductBySlug, createTicketOrder } from "@/lib/tickets.functions";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { TicketNotFound } from "@/components/TicketNotFound";

export const Route = createFileRoute("/tickets/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: `book ${params.slug.replace(/-/g, " ")} — tickets | what` },
      { name: "description", content: "Choose a date and book your ticket for Helsinki sightseeing." },
    ],
  }),
  component: TicketBookingPage,
});

function formatEUR(cents: number) {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function operatorLabel(op: string) {
  if (op === "royalline") return "Royalline.fi";
  if (op === "citytour") return "Citytour.fi";
  return "Royalline.fi + Citytour.fi";
}

function operatorIcon(op: string) {
  if (op === "royalline") return <Bus className="size-4" />;
  if (op === "citytour") return <Ship className="size-4" />;
  return <Sparkles className="size-4" />;
}

function TicketBookingPage() {
  const { slug } = Route.useParams();
  const navigate = useNavigate();
  const fetchProduct = useServerFn(getTicketProductBySlug);
  const createOrder = useServerFn(createTicketOrder);

  const { data, isLoading } = useQuery({
    queryKey: ["ticket-product", slug],
    queryFn: () => fetchProduct({ data: { slug } }),
  });

  const product = data?.product;

  const [date, setDate] = useState<Date | undefined>();
  const [dateInput, setDateInput] = useState<string>("");
  const [dateError, setDateError] = useState<string | null>(null);
  const [dateOpen, setDateOpen] = useState(false);

  // auto-insert dots so typing "27062026" becomes "27.06.2026"
  function formatDateTyping(raw: string): string {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}.${digits.slice(2)}`;
    return out;
  }

  function parseManualDate(raw: string): Date | null {
    const s = raw.trim().replace(/[\/\-\s]/g, ".");
    const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (!m) return null;
    const d = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const y = parseInt(m[3], 10);
    if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
    return dt;
  }

  // live verify on every keystroke — only commits date if fully valid + not in past
  function handleDateInputChange(raw: string) {
    const formatted = formatDateTyping(raw);
    setDateInput(formatted);

    if (!formatted) { setDate(undefined); setDateError(null); return; }

    const digits = formatted.replace(/\D/g, "");

    // partial day check (e.g. "32" can't be a day)
    if (digits.length >= 2) {
      const d = parseInt(digits.slice(0, 2), 10);
      if (d < 1 || d > 31) { setDate(undefined); setDateError("day must be 01-31"); return; }
    }
    // partial month check
    if (digits.length >= 4) {
      const mo = parseInt(digits.slice(2, 4), 10);
      if (mo < 1 || mo > 12) { setDate(undefined); setDateError("month must be 01-12"); return; }
    }
    // partial year hint (current or future)
    if (digits.length >= 8) {
      const dt = parseManualDate(formatted);
      if (!dt) { setDate(undefined); setDateError("that date doesn't exist"); return; }
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const maxYear = today.getFullYear() + 2;
      if (dt.getFullYear() < today.getFullYear() || dt.getFullYear() > maxYear) {
        setDate(undefined); setDateError(`year must be ${today.getFullYear()}-${maxYear}`); return;
      }
      if (dt < today) { setDate(undefined); setDateError("date is in the past"); return; }
      setDateError(null);
      setDate(dt);
      return;
    }

    // not complete yet — clear error but no committed date
    setDate(undefined);
    setDateError(null);
  }
  const [quantity, setQuantity] = useState(1);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerAge, setBuyerAge] = useState<number | "">("");
  const [holderNames, setHolderNames] = useState<string[]>([""]);
  const [holderAges, setHolderAges] = useState<(number | "")[]>([""]);
  const [submitting, setSubmitting] = useState(false);

  function updateQuantity(q: number) {
    const next = Math.max(1, Math.min(10, q));
    setQuantity(next);
    setHolderNames((prev) => {
      const arr = [...prev];
      while (arr.length < next) arr.push("");
      arr.length = next;
      return arr;
    });
    setHolderAges((prev) => {
      const arr = [...prev];
      while (arr.length < next) arr.push("");
      arr.length = next;
      return arr;
    });
  }

  function setHolderName(i: number, val: string) {
    setHolderNames((prev) => prev.map((h, idx) => (idx === i ? val : h)));
  }

  function setHolderAge(i: number, val: string) {
    const trimmed = val.trim();
    if (trimmed === "") {
      setHolderAges((prev) => prev.map((a, idx) => (idx === i ? "" : a)));
      return;
    }
    const n = Math.max(0, Math.min(120, Math.floor(Number(trimmed))));
    if (Number.isNaN(n)) return;
    setHolderAges((prev) => prev.map((a, idx) => (idx === i ? n : a)));
  }

  function ageCategory(age: number | ""): { label: string; tone: string } | null {
    if (age === "") return null;
    if (age <= 2) return { label: "baby", tone: "bg-pink-100 text-pink-700 border-pink-200" };
    if (age <= 12) return { label: "child", tone: "bg-amber-100 text-amber-700 border-amber-200" };
    if (age <= 17) return { label: "teen", tone: "bg-sky-100 text-sky-700 border-sky-200" };
    if (age <= 64) return { label: "adult", tone: "bg-emerald-100 text-emerald-700 border-emerald-200" };
    return { label: "senior", tone: "bg-violet-100 text-violet-700 border-violet-200" };
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!product) return;
    if (!date) { toast.error("Please pick a date"); return; }
    if (!buyerName.trim()) { toast.error("Please enter your name"); return; }
    if (!buyerEmail.trim()) { toast.error("Please enter your email"); return; }
    if (holderNames.some((n) => !n.trim())) {
      // auto-fill blank holder names with buyer name
      const filled = holderNames.map((n) => n.trim() || buyerName.trim());
      setHolderNames(filled);
    }
    const finalHolderNames = holderNames.map((n) => n.trim() || buyerName.trim());

    setSubmitting(true);
    try {
      const res = await createOrder({
        data: {
          productSlug: product.slug,
          validDate: format(date, "yyyy-MM-dd"),
          buyerName: buyerName.trim(),
          buyerEmail: buyerEmail.trim(),
          holderNames: finalHolderNames,
        },
      });
      navigate({ to: "/tickets/confirmation/$orderId", params: { orderId: res.orderId } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not create order");
      setSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16">
        <p className="text-sm text-muted-foreground">loading…</p>
      </div>
    );
  }

  if (!product) {
    return <TicketNotFound attemptedSlug={slug} />;
  }

  const totalCents = product.price_cents * quantity;
  const paymentUrl = (product as any).payment_url as string | null | undefined;

  const glassCard =
    "relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/90 via-white/75 to-white/55 backdrop-blur-xl ring-1 ring-white/60 shadow-[0_10px_30px_-14px_rgba(0,53,128,0.18),inset_0_1px_0_rgba(255,255,255,0.9)]";
  const glassSheen = (
    <>
      <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/70 to-transparent" />
      <div className="pointer-events-none absolute -top-10 -right-10 h-40 w-40 rounded-full bg-[#003580]/10 blur-3xl" />
    </>
  );

  return (
    <div
      className="relative font-sans normal-case"
      style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif" }}
    >
      {/* ambient page background */}
      <div className="pointer-events-none fixed inset-0 -z-10 bg-gradient-to-b from-[#eef3fb] via-[#f7faff] to-white" />
      <div className="pointer-events-none fixed -top-40 -left-40 -z-10 h-[480px] w-[480px] rounded-full bg-[#003580]/10 blur-[120px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 -z-10 h-[520px] w-[520px] rounded-full bg-[#3b6fa0]/10 blur-[120px]" />

      <div className="max-w-5xl mx-auto px-3 sm:px-6 pt-4 md:pt-10 pb-32 md:pb-20">
        <Link
          to="/tickets"
          className="inline-flex items-center gap-2 text-xs font-medium text-[#003580]/70 hover:text-[#003580] mb-4 md:mb-6 normal-case"
        >
          <ArrowLeft className="size-3.5" /> all tickets
        </Link>

        <div className="grid md:grid-cols-5 gap-5 md:gap-8">
          {/* product card */}
          <aside className="md:col-span-2">
            <div className={cn(glassCard, "md:sticky md:top-24")}>
              {glassSheen}
              <div className="relative aspect-[16/10] md:aspect-[4/3] bg-gradient-to-br from-[#001a44] via-[#00367f] to-[#1e5fbf] overflow-hidden">
                {product.image_url ? (
                  <img src={product.image_url} alt={product.name} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/40">
                    {operatorIcon(product.operator)}
                  </div>
                )}
                <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/15 to-transparent" />
              </div>
              <div className="relative p-4 md:p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 ring-1 ring-[#003580]/15 text-[#003580] text-[11px] font-medium">
                    {operatorIcon(product.operator)}
                    <span>{operatorLabel(product.operator).toLowerCase()}</span>
                  </div>
                  {(product as any).badge && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#003580] text-white text-[11px] font-medium">
                      {String((product as any).badge).toLowerCase()}
                    </span>
                  )}
                </div>
                <h1 className="mt-3 text-2xl font-semibold tracking-tight text-[#003580] leading-tight">
                  {product.name.toLowerCase()}
                </h1>
                {(product as any).subtitle && (
                  <p className="mt-1 text-sm text-muted-foreground">{(product as any).subtitle}</p>
                )}
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{product.description}</p>
                {Array.isArray((product as any).highlights) && (product as any).highlights.length > 0 && (
                  <ul className="mt-4 space-y-1.5">
                    {(product as any).highlights.map((h: string) => (
                      <li key={h} className="text-xs text-muted-foreground inline-flex items-start gap-1.5">
                        <BadgeCheck className="size-3.5 mt-0.5 text-[#003580]/70 flex-shrink-0" /> {h}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-5 relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br from-[#fff8e6] via-[#ffeec2] to-[#f5d98a] ring-1 ring-[#c9a84c]/40 shadow-[0_8px_20px_-10px_rgba(201,168,76,0.45),inset_0_1px_0_rgba(255,255,255,0.85)]">
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-white/70 to-transparent" />
                  <div className="pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full bg-white/50 blur-2xl" />
                  <div className="relative flex items-baseline justify-between">
                    <span className="text-[11px] font-medium text-[#7a5a10]">per ticket</span>
                    <span className="text-2xl font-semibold text-[#5a3f00] tracking-tight">{formatEUR(product.price_cents)}</span>
                  </div>
                  {Array.isArray((product as any).age_tiers) && (product as any).age_tiers.length > 0 && (
                    <div className="relative mt-3 pt-3 border-t border-[#c9a84c]/30 space-y-1.5">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#7a5a10]">prices by age</div>
                      {(product as any).age_tiers.map((t: any, i: number) => (
                        <div key={i} className="flex items-center justify-between text-xs text-[#5a3f00]">
                          <span>
                            <strong className="capitalize">{t.label}</strong>{" "}
                            <span className="text-[#7a5a10]/80">({t.min_age}–{t.max_age} yrs)</span>
                          </span>
                          <span className="font-semibold tabular-nums">{formatEUR(t.price_cents)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(product as any).affiliate_url && (
                    <a
                      href={(product as any).affiliate_url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="relative mt-3 inline-flex items-center gap-1 text-[11px] text-[#7a5a10] hover:text-[#5a3f00]"
                    >
                      book direct with {operatorLabel(product.operator).toLowerCase()} <ExternalLink className="size-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </aside>

          {/* right column — booking */}
          {(() => {
            const ageTiers = Array.isArray((product as any).age_tiers) ? (product as any).age_tiers as any[] : [];
            const tierLinks = ageTiers.filter((t) => t && t.payment_url);
            const hasAnyLink = !!paymentUrl || tierLinks.length > 0;

            if (!hasAnyLink) {
              return (
                <div className="md:col-span-3">
                  <div className={cn(glassCard, "p-6 md:p-8 flex flex-col justify-center min-h-[260px]")}>
                    {glassSheen}
                    <div className="relative inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 ring-1 ring-red-200 text-red-700 text-[11px] font-medium w-fit">
                      booking unavailable
                    </div>
                    <h2 className="relative mt-3 text-2xl font-semibold tracking-tight text-[#003580]">this ticket isn't bookable yet</h2>
                    <p className="relative mt-2 text-sm text-muted-foreground">
                      the operator hasn't published a payment link for this product. please check back soon or contact us.
                    </p>
                    <div className="relative mt-6 flex flex-wrap gap-3">
                      <Link
                        to="/tickets"
                        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-[#003580] text-white text-sm font-medium hover:bg-[#002a66] transition-colors"
                      >
                        <ArrowLeft className="size-4" /> browse other tickets
                      </Link>
                      <Link
                        to="/contact"
                        className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-white text-[#003580] border border-[#003580]/20 text-sm font-medium hover:bg-[#eef3fb] transition-colors"
                      >
                        contact us
                      </Link>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div className="md:col-span-3">
                <div className={cn(glassCard, "p-6 md:p-8 flex flex-col justify-center min-h-[260px]")}>
                  {glassSheen}
                  <h2 className="relative text-2xl font-semibold tracking-tight text-[#003580]">buy ticket</h2>
                  <p className="relative mt-2 text-sm text-muted-foreground">checkout opens at our partner's secure page.</p>

                  {/* primary buy button (uses main payment_url if set, else first tier link) */}
                  {paymentUrl && (
                    <a
                      href={paymentUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="relative mt-6 inline-flex w-fit items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#003580] text-white text-sm font-medium shadow-[0_10px_24px_-10px_rgba(0,53,128,0.55),inset_0_1px_0_rgba(255,255,255,0.25)] hover:bg-[#002a66] transition-colors"
                    >
                      buy now — {formatEUR(product.price_cents)} <ArrowUpRight className="size-4" />
                    </a>
                  )}

                  {/* per-tier buy buttons */}
                  {tierLinks.length > 0 && (
                    <div className="relative mt-5 space-y-2">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#003580]/60">
                        by age category
                      </div>
                      {ageTiers.map((t: any, i: number) => (
                        <div
                          key={i}
                          className="flex items-center justify-between gap-3 rounded-2xl bg-white/70 backdrop-blur ring-1 ring-[#003580]/10 px-4 py-3"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-[#003580] capitalize">{t.label}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {t.min_age}–{t.max_age} yrs · {formatEUR(t.price_cents)}
                            </div>
                          </div>
                          {t.payment_url ? (
                            <a
                              href={t.payment_url}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-[#003580] text-white text-xs font-medium hover:bg-[#002a66] transition-colors"
                            >
                              buy <ArrowUpRight className="size-3.5" />
                            </a>
                          ) : paymentUrl ? (
                            <a
                              href={paymentUrl}
                              target="_blank"
                              rel="noreferrer noopener"
                              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-white text-[#003580] border border-[#003580]/20 text-xs font-medium hover:bg-[#eef3fb] transition-colors"
                            >
                              buy <ArrowUpRight className="size-3.5" />
                            </a>
                          ) : (
                            <span className="text-[11px] text-muted-foreground">no link</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <p className="relative mt-4 text-xs text-muted-foreground">
                    valid the day you choose at checkout.
                  </p>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
